/**
 * roleUtils.js — Saige Role-Aware Workspace Utility
 *
 * Derives a unified user role from available signals:
 *   1. `saige_role` in localStorage (set at login by /check-user)
 *   2. `userProfile.role_variant` (future: backend-issued role type)
 *   3. `userProfile.role` (fallback field)
 *
 * Role enum:
 *   'teacher'      — default, lesson planning + grading
 *   'admin'        — district_admin or campus_admin
 *   'counselor'    — forward-looking; reads role_variant if present
 *   'support_staff'— forward-looking; reads role_variant if present
 *
 * All paths fall back to 'teacher' if no signal is found.
 * No backend schema change is required for initial rollout.
 */

/** @returns {'teacher'|'admin'|'counselor'|'support_staff'} */
export function getUserRoleVariant(userProfile) {
    // 1. Check backend-issued admin role (most authoritative)
    try {
        const storedRole = localStorage.getItem('saige_role') || '';
        if (storedRole === 'district_admin' || storedRole === 'campus_admin') {
            return 'admin';
        }
    } catch { /* localStorage unavailable */ }

    // 2. Check profile-level role variant (forward-looking; backend not yet issuing these)
    const variant = userProfile?.role_variant || userProfile?.role;
    if (variant === 'counselor') return 'counselor';
    if (variant === 'support_staff' || variant === 'support') return 'support_staff';
    if (variant === 'district_admin' || variant === 'campus_admin') return 'admin';

    // 3. Default: teacher
    return 'teacher';
}


// ─── Labels ──────────────────────────────────────────────────────────────────

export const ROLE_LABELS = {
    teacher:       'Teacher',
    admin:         'Admin',
    counselor:     'Counselor',
    support_staff: 'Support Staff',
};

export const ROLE_SUBTITLES = {
    teacher:       "Let's plan tomorrow's lesson.",
    admin:         "Here's what needs your attention today.",
    counselor:     "How can you support students today?",
    support_staff: "What does campus need today?",
};


// ─── Assistant Starter Prompts (by role) ─────────────────────────────────────

export const ROLE_STARTER_PROMPTS = {
    teacher: [
        { icon: '📅', label: 'Help me plan tomorrow',      prompt: "Help me plan what to teach tomorrow. I'd like a quick suggestion based on my subject and grade." },
        { icon: '🎯', label: 'What should I teach next?',  prompt: "Based on my subject and grade level, what are some good topics to cover next? Give me 3 options with a brief rationale for each." },
        { icon: '🔍', label: 'Simplify a topic',           prompt: "Help me simplify a topic for my students. I'll tell you the topic and you give me a plain-language explanation suitable for my grade level." },
        { icon: '📊', label: 'Grade safely with Saige',    prompt: "How can I use Saige to help with grading while keeping student data private? Walk me through a safe approach." },
        { icon: '🏫', label: "What context is Saige using?", prompt: "What context, documents, and information is Saige using for me? Summarize what you know about my teaching profile." },
    ],
    admin: [
        { icon: '🚨', label: "What needs attention today?",  prompt: "What are the highest-priority watch items or open issues on campus right now? Give me a concise action list." },
        { icon: '📈', label: 'Campus usage this week',        prompt: "Summarize how teachers on campus have been using Saige this week. Are there any patterns or concerns worth noting?" },
        { icon: '📋', label: 'Open issues summary',           prompt: "What are the current open issues or unresolved feedback items I should review? Prioritize by urgency." },
        { icon: '📣', label: 'Draft staff announcement',      prompt: "Help me draft a clear and professional announcement for my campus staff. I'll tell you the topic." },
        { icon: '🔍', label: 'Check context Saige has',       prompt: "What campus and district context is Saige currently using for this campus? What might be missing?" },
    ],
    counselor: [
        { icon: '💬', label: 'Draft parent communication',    prompt: "Help me draft a professional and empathetic communication to a parent. I'll describe the situation." },
        { icon: '📋', label: 'What referral procedures apply?', prompt: "What are the current referral or escalation procedures I should follow? Help me identify the right process for a student situation." },
        { icon: '🗓️', label: 'Upcoming important dates',      prompt: "What are the upcoming campus dates I should be aware of — testing, events, deadlines? Summarize what's relevant for this week and next." },
        { icon: '🆘', label: 'Campus support resources',       prompt: "What campus and district support resources are available for students? Give me a quick reference." },
        { icon: '✅', label: 'Schedule a student check-in',   prompt: "Help me prepare for a student check-in conversation. What questions should I ask, and what context should I gather first?" },
    ],
    support_staff: [
        { icon: '📋', label: 'Campus procedures today',       prompt: "What are the key campus procedures or protocols I should know about today? Summarize what's most relevant." },
        { icon: '📞', label: 'Who do I contact for this?',   prompt: "I need to find the right person or department to contact. Describe what you need and I'll help identify who to reach." },
        { icon: '🗓️', label: "This week's campus calendar",  prompt: "What's on the campus calendar this week? Give me a concise overview of events, deadlines, and anything staff should prepare for." },
        { icon: '📣', label: 'Draft campus communication',    prompt: "Help me write a clear and professional message for campus staff or families. I'll give you the context." },
        { icon: '🔍', label: 'Find campus info fast',         prompt: "I'm looking for specific campus information — policies, contacts, or procedures. Tell me what you need and I'll help locate it." },
    ],
};


// ─── Role-Aware Assistant Welcome Messages ───────────────────────────────────

export function getRoleWelcomeMessage(userRole, userName) {
    const first = userName?.split(' ')[0] || '';
    const greeting = first ? ` ${first}` : '';

    switch (userRole) {
        case 'admin':
            return `Hi${greeting}! I'm your Saige assistant. I can help you track campus activity, draft communications, or review what's happening across your school.\n\nWhat would you like to look at?`;
        case 'counselor':
            return `Hi${greeting}! I'm your Saige assistant. I can help you support students, draft parent communications, or navigate campus procedures and resources.\n\nWhat do you need today?`;
        case 'support_staff':
            return `Hi${greeting}! I'm your Saige assistant. I can help you navigate campus procedures, find the right contacts, or draft communications.\n\nWhat can I help with?`;
        default:
            return `Hi${greeting}! I'm your Saige assistant. I can help you plan lessons, think through topics, or answer questions about using Saige effectively.\n\nWhat would you like help with today?`;
    }
}


// ─── Campus Hub Prominence by Role ───────────────────────────────────────────

/**
 * Returns how prominent the CampusIntelligenceHub should be for a given role.
 * 'primary'   — above the fold, full section
 * 'secondary' — below primary actions, collapsible
 */
export function getCampusHubProminence(userRole) {
    switch (userRole) {
        case 'admin':
        case 'counselor':
        case 'support_staff':
            return 'primary';
        default:
            return 'secondary';
    }
}

/**
 * Teacher context is present when a user has enough teaching metadata
 * to safely expose the lesson-planning workspace alongside role-specific UI.
 */
export function hasTeacherContext({ subjects = [], gradeLevel = '', userProfile = {} } = {}) {
    const normalizedSubjects = Array.isArray(subjects) ? subjects.filter(Boolean) : [];
    const profileSubjects = Array.isArray(userProfile?.subjects) ? userProfile.subjects.filter(Boolean) : [];
    const normalizedGradeLevel = typeof gradeLevel === 'string' ? gradeLevel.trim() : '';
    const profileGradeLevel = typeof userProfile?.grade_level === 'string' ? userProfile.grade_level.trim() : '';
    const profileGrade = typeof userProfile?.grade === 'string' ? userProfile.grade.trim() : '';
    const profileSubject = typeof userProfile?.subject === 'string' ? userProfile.subject.trim() : '';

    return (
        normalizedSubjects.length > 0
        || profileSubjects.length > 0
        || normalizedGradeLevel.length > 0
        || profileGradeLevel.length > 0
        || profileGrade.length > 0
        || profileSubject.length > 0
    );
}

/**
 * Controls whether the lesson-generation workspace should render.
 * Teachers always keep access. Admin users gain it only when teacher
 * context is present. All other fallback roles preserve prior behavior.
 */
export function shouldRenderTeacherWorkspace({
    userRole,
    subjects = [],
    gradeLevel = '',
    userProfile = {},
} = {}) {
    if (userRole === 'teacher') {
        return true;
    }

    if (userRole === 'admin') {
        return hasTeacherContext({ subjects, gradeLevel, userProfile });
    }

    return !['counselor', 'support_staff'].includes(userRole);
}

export function shouldRenderAdminWorkspace(userRole) {
    return userRole === 'admin';
}
